import { useMemo, useState } from "react"
import { AdminLayout } from "@/components/Admin/AdminLayout"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { router } from "@inertiajs/react"
import { BarChart, Bar, PieChart as RePieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts"
import { Download, RefreshCw, Filter, BarChart3, PieChart, ExternalLink, Users, TrendingUp } from "lucide-react"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"

interface TestResult {
  id: number
  user_id: number
  nome: string | null
  email: string | null
  telefone: string | null
  cidade: string | null
  escola: string | null
  serie: string | null
  sala: string | null
  data_nascimento: string | null
  teste_basico_completo: boolean
  teste_avancado_completo: boolean
  tipo_eneagrama: number | null
  area_vocacional: string | null
  microperfil: string | null
  pontuacoes_eneagrama: Record<string, number> | null
  pontuacoes_avancadas: Record<string, number> | null
  created_at: string
  teste_basico_inicio_at: string | null
  teste_basico_fim_at: string | null
  teste_avancado_inicio_at: string | null
  teste_avancado_fim_at: string | null
}

interface Props {
  stats: {
    total_users: number
    total_orders: number
    paid_orders: number
    pending_orders: number
    failed_orders: number
    total_revenue_cents: number
    recent_orders: Array<{
      id: number
      user_email: string
      user_name: string
      total_cents: number
      status: string
      created_at: string
      paid_at?: string | null
    }>
  }
  test_results: TestResult[]
  results_offer_active: boolean
}

const HEBAG_COLORS: Record<string, string> = {
  H: "#3b82f6", E: "#8b5cf6", B: "#22c55e", A: "#f59e0b", G: "#64748b",
}

const HEBAG_NAMES: Record<string, string> = {
  H: "Humanas", E: "Exatas", B: "Biológicas", A: "Artes", G: "Gestão",
}

const HEBAG_KEYS = ["H", "E", "B", "A", "G"] as const

const formatDate = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(date)
}

const formatDateTime = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(date)
}

const getAge = (value?: string | null) => {
  if (!value) return null
  const birth = new Date(value)
  if (Number.isNaN(birth.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1
  return age
}

export default function Dashboard({ test_results, results_offer_active }: Props) {
  const [offerActive, setOfferActive] = useState(results_offer_active)
  const [selectedStudents, setSelectedStudents] = useState<Set<number>>(
    new Set(test_results.map((result) => result.id))
  )
  const [filterEscola, setFilterEscola] = useState("all")
  const [filterSerie, setFilterSerie] = useState("all")
  const [sortOrder, setSortOrder] = useState<"alphabetical" | "date">("alphabetical")
  const [reportEscola, setReportEscola] = useState("")
  const [reportSerie, setReportSerie] = useState("")
  const [reportSala, setReportSala] = useState("")

  const escolas = useMemo(() => {
    return Array.from(new Set(test_results.map((r) => r.escola).filter(Boolean) as string[])).sort()
  }, [test_results])

  const series = useMemo(() => {
    return Array.from(new Set(test_results.map((r) => r.serie).filter(Boolean) as string[])).sort()
  }, [test_results])

  const salas = useMemo(() => {
    return Array.from(new Set(test_results.map((r) => r.sala).filter(Boolean) as string[])).sort()
  }, [test_results])

  const statsSummary = useMemo(() => {
    const typeDistribution: Record<number, number> = {}
    test_results.forEach((result) => {
      if (result.tipo_eneagrama) {
        typeDistribution[result.tipo_eneagrama] = (typeDistribution[result.tipo_eneagrama] || 0) + 1
      }
    })

    const sortedTypes = Object.entries(typeDistribution).sort((a, b) => b[1] - a[1])
    const mostCommonType = sortedTypes.length > 0 ? { type: Number(sortedTypes[0][0]), count: sortedTypes[0][1] } : null

    return {
      totalTests: test_results.length,
      basicTests: test_results.filter((r) => r.teste_basico_completo).length,
      advancedTests: test_results.filter((r) => r.teste_avancado_completo).length,
      paidUsers: test_results.filter((r) => r.teste_avancado_completo).length,
      typeDistribution,
      mostCommonType,
    }
  }, [test_results])

  const getFilteredResults = () => {
    const filtered = test_results.filter((result) => {
      if (!selectedStudents.has(result.id)) return false
      if (filterEscola !== "all" && result.escola !== filterEscola) return false
      if (filterSerie !== "all" && result.serie !== filterSerie) return false
      return true
    })

    return filtered.sort((a, b) => {
      if (sortOrder === "alphabetical") {
        return (a.nome || "").localeCompare(b.nome || "", "pt-BR")
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
  }

  const toggleStudent = (id: number) => {
    const next = new Set(selectedStudents)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedStudents(next)
  }

  const toggleAllStudents = () => {
    if (selectedStudents.size === test_results.length) {
      setSelectedStudents(new Set())
    } else {
      setSelectedStudents(new Set(test_results.map((r) => r.id)))
    }
  }

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" })
    doc.setFontSize(16)
    doc.text("Relatório Admin - Orientação Profissional", 14, 16)

    const rows = getFilteredResults().map((result) => [
      result.nome || "-",
      result.email || "-",
      result.escola || "-",
      result.serie || "-",
      result.sala || "-",
      result.tipo_eneagrama ? `Tipo ${result.tipo_eneagrama}` : "-",
      result.area_vocacional || "-",
      result.microperfil || "-",
      result.teste_basico_fim_at ? formatDateTime(result.teste_basico_fim_at) : "-",
      result.teste_avancado_fim_at ? formatDateTime(result.teste_avancado_fim_at) : "-",
    ])

    autoTable(doc, {
      startY: 24,
      head: [["Nome", "E-mail", "Escola", "Série", "Sala", "Tipo", "Área", "Microperfil", "Básico", "Avançado"]],
      body: rows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [70, 70, 70] },
    })

    doc.save("relatório-admin.pdf")
  }

  const getReportData = () => {
    return test_results.filter((result) => {
      if (reportEscola && result.escola !== reportEscola) return false
      if (reportSerie && result.serie !== reportSerie) return false
      if (reportSala && result.sala !== reportSala) return false
      return true
    })
  }

  const reportResults = getReportData()

  const reportDistribution = useMemo(() => {
    const counts: Record<number, number> = {}
    reportResults.forEach((result) => {
      if (result.tipo_eneagrama) {
        counts[result.tipo_eneagrama] = (counts[result.tipo_eneagrama] || 0) + 1
      }
    })
    return Object.entries(counts)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([type, count]) => ({ name: `Tipo ${type}`, value: count }))
  }, [reportResults])

  const reportAdvancedResults = useMemo(() => {
    return reportResults.filter((r) => r.teste_avancado_completo && r.pontuacoes_avancadas)
  }, [reportResults])

  const reportHebagDistribution = useMemo(() => {
    const counts: Record<string, number> = { H: 0, E: 0, B: 0, A: 0, G: 0 }
    reportAdvancedResults.forEach((r) => {
      const scores = r.pontuacoes_avancadas!
      let maxKey = "H"
      let maxVal = -1
      for (const key of HEBAG_KEYS) {
        const val = scores[key] ?? 0
        if (val > maxVal) { maxVal = val; maxKey = key }
      }
      counts[maxKey] = (counts[maxKey] || 0) + 1
    })
    return HEBAG_KEYS.map((key) => ({ name: HEBAG_NAMES[key], area: key, value: counts[key] }))
  }, [reportAdvancedResults])

  const reportMicroperfilDistribution = useMemo(() => {
    const counts: Record<string, number> = {}
    reportAdvancedResults.forEach((r) => {
      if (r.microperfil) {
        counts[r.microperfil] = (counts[r.microperfil] || 0) + 1
      }
    })
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }))
  }, [reportAdvancedResults])

  const reportHebagAverages = useMemo(() => {
    if (reportAdvancedResults.length === 0) return []
    const totals: Record<string, number> = { H: 0, E: 0, B: 0, A: 0, G: 0 }
    reportAdvancedResults.forEach((r) => {
      const scores = r.pontuacoes_avancadas!
      for (const key of HEBAG_KEYS) {
        totals[key] += scores[key] ?? 0
      }
    })
    const count = reportAdvancedResults.length
    return HEBAG_KEYS.map((key) => ({
      name: HEBAG_NAMES[key],
      area: key,
      value: Math.round((totals[key] / count) * 10) / 10,
    }))
  }, [reportAdvancedResults])

  const filteredResults = getFilteredResults()

  return (
    <AdminLayout title="Painel Administrativo">
      <Card className="p-6 mb-6 border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-amber-900">Botão de venda na página de resultados</h2>
            <p className="text-sm text-amber-700">
              Controle se o botão de venda aparece na seção final de /results.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              type="button"
              onClick={() => {
                const next = !offerActive
                setOfferActive(next)
                router.patch("/admin/feature-flags/offer", { enabled: next })
              }}
              aria-pressed={offerActive}
              className={`relative w-20 h-9 rounded-full p-1 transition-colors ${
                offerActive ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-300 hover:bg-slate-400"
              }`}
            >
              <span className="sr-only">Ativar vendas</span>
              <span
                className={`absolute top-1 left-1 h-7 w-7 rounded-full bg-white shadow transition-transform ${
                  offerActive ? "translate-x-11" : "translate-x-0"
                }`}
              />
            </Button>
            <span className="text-sm font-semibold text-amber-900">Ativar vendas</span>
            <span className={`text-sm font-semibold ${offerActive ? "text-green-700" : "text-red-700"}`}>
              {offerActive ? "Ativado" : "Desativado"}
            </span>
          </div>
        </div>
      </Card>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
            <select
              onChange={(event) => {
                if (event.target.value) router.visit(event.target.value)
              }}
              className="border rounded-md px-3 py-2 text-sm bg-white"
              defaultValue=""
            >
              <option value="" disabled>Ir para página...</option>
              <option value="/">Início</option>
              <option value="/admin">Admin</option>
              <option value="/admin/orders">Pedidos</option>
              <option value="/admin/test_results">Resultados</option>
            </select>
          </div>
          <Button onClick={() => router.reload()} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </Button>
        </div>
        <Button onClick={exportToPDF} className="gap-2">
          <Download className="h-4 w-4" />
          Exportar PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-full">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total de Testes</p>
              <p className="text-2xl font-bold">{statsSummary.totalTests}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/10 rounded-full">
              <TrendingUp className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Testes Básicos</p>
              <p className="text-2xl font-bold">{statsSummary.basicTests}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-500/10 rounded-full">
              <TrendingUp className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Testes Avançados</p>
              <p className="text-2xl font-bold">{statsSummary.advancedTests}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground mb-1">Tipo mais comum</p>
          {statsSummary.mostCommonType ? (
            <div>
              <p className="text-2xl font-bold">Tipo {statsSummary.mostCommonType.type}</p>
              <p className="text-sm text-muted-foreground">{statsSummary.mostCommonType.count} pessoas</p>
            </div>
          ) : (
            <p className="text-sm">Sem dados</p>
          )}
        </Card>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-3">
          <TabsTrigger value="users">Usuários</TabsTrigger>
          <TabsTrigger value="stats">Estatísticas</TabsTrigger>
          <TabsTrigger value="report">Relatório por turma</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold">Filtros e seleção</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium mb-2 block">Ordenar por</label>
                <select
                  value={sortOrder}
                  onChange={(event) => setSortOrder(event.target.value as "alphabetical" | "date")}
                  className="border rounded-md px-3 py-2 w-full text-sm bg-white"
                >
                  <option value="alphabetical">Ordem alfabética</option>
                  <option value="date">Data (mais recente)</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Colégio</label>
                <select
                  value={filterEscola}
                  onChange={(event) => setFilterEscola(event.target.value)}
                  className="border rounded-md px-3 py-2 w-full text-sm bg-white"
                >
                  <option value="all">Todos os colégios</option>
                  {escolas.map((escola) => (
                    <option key={escola} value={escola}>{escola}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Série</label>
                <select
                  value={filterSerie}
                  onChange={(event) => setFilterSerie(event.target.value)}
                  className="border rounded-md px-3 py-2 w-full text-sm bg-white"
                >
                  <option value="all">Todas as séries</option>
                  {series.map((serie) => (
                    <option key={serie} value={serie}>{serie}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <Button variant="outline" onClick={toggleAllStudents} className="w-full">
                  {selectedStudents.size === test_results.length ? "Desmarcar todos" : "Marcar todos"}
                </Button>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              {selectedStudents.size} de {test_results.length} alunos selecionados
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-bold mb-4">
              Lista de usuários {sortOrder === "alphabetical" ? "(ordem alfabética)" : "(data)"}
            </h2>

            {filteredResults.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhum resultado encontrado</p>
            ) : (
              <div className="space-y-4">
                {filteredResults.map((result) => {
                  const basicTime = result.teste_basico_inicio_at && result.teste_basico_fim_at
                    ? Math.round((new Date(result.teste_basico_fim_at).getTime() - new Date(result.teste_basico_inicio_at).getTime()) / 60000)
                    : null
                  const advancedTime = result.teste_avancado_inicio_at && result.teste_avancado_fim_at
                    ? Math.round((new Date(result.teste_avancado_fim_at).getTime() - new Date(result.teste_avancado_inicio_at).getTime()) / 60000)
                    : null
                  const age = getAge(result.data_nascimento)

                  return (
                    <Card key={result.id} className="p-4">
                      <div className="flex items-start gap-4">
                        <input
                          type="checkbox"
                          checked={selectedStudents.has(result.id)}
                          onChange={() => toggleStudent(result.id)}
                          className="mt-1 h-4 w-4 accent-primary"
                        />
                        <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">{result.nome || "Sem nome"}</h3>
                              {result.teste_avancado_completo && (
                                <span className="px-2 py-1 rounded-full text-xs bg-orange-500 text-white">Avançado</span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{result.email}</p>
                            {result.escola && result.serie && (
                              <p className="text-sm text-muted-foreground">
                                {result.escola} - {result.serie}{result.sala ? ` (${result.sala})` : ""}
                              </p>
                            )}
                            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                              {age !== null && <span>Idade: {age}</span>}
                              {basicTime !== null && <span>Básico: {basicTime} min</span>}
                              {advancedTime !== null && <span>Avançado: {advancedTime} min</span>}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <p className="text-sm text-muted-foreground">{formatDateTime(result.created_at)}</p>
                            {result.tipo_eneagrama && (
                              <span className="px-2 py-1 rounded-full text-xs border">Tipo {result.tipo_eneagrama}</span>
                            )}
                            {result.area_vocacional && (
                              <span className="px-2 py-1 rounded-full text-xs bg-muted">{result.area_vocacional}</span>
                            )}
                            {result.microperfil && (
                              <span className="px-2 py-1 rounded-full text-xs bg-blue-500 text-white">{result.microperfil}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="stats" className="mt-6 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold">Resumo geral</h2>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { name: "Total", value: statsSummary.totalTests, fill: "hsl(var(--primary))" },
                    { name: "Básicos", value: statsSummary.basicTests, fill: "hsl(217, 91%, 60%)" },
                    { name: "Avançados", value: statsSummary.advancedTests, fill: "hsl(25, 85%, 53%)" },
                  ]}
                >
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold">Proporção dos tipos Eneagrama</h2>
            </div>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={Object.entries(
                    filteredResults.reduce((acc, result) => {
                      if (result.tipo_eneagrama) {
                        acc[result.tipo_eneagrama] = (acc[result.tipo_eneagrama] || 0) + 1
                      }
                      return acc
                    }, {} as Record<number, number>)
                  ).sort((a, b) => Number(a[0]) - Number(b[0]))
                    .map(([type, count]) => ({
                      name: `Tipo ${type}`,
                      quantidade: count,
                      porcentagem: Math.round((count / (filteredResults.length || 1)) * 100),
                    }))}
                >
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="quantidade" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <PieChart className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold">Distribuição (Pizza)</h2>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={Object.entries(statsSummary.typeDistribution).map(([type, count]) => ({
                      name: `Tipo ${type}`,
                      value: count,
                    }))}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={110}
                    label
                  >
                    {Object.entries(statsSummary.typeDistribution).map(([type], index) => (
                      <Cell key={`cell-${type}`} fill={["#7c3aed", "#2563eb", "#f97316", "#10b981", "#ec4899", "#0ea5e9", "#eab308", "#ef4444", "#22c55e"][index % 9]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RePieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="report" className="mt-6 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold">Relatório por turma</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Colégio</label>
                <select
                  value={reportEscola}
                  onChange={(event) => setReportEscola(event.target.value)}
                  className="border rounded-md px-3 py-2 w-full text-sm bg-white"
                >
                  <option value="">Todos</option>
                  {escolas.map((escola) => (
                    <option key={escola} value={escola}>{escola}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Série</label>
                <select
                  value={reportSerie}
                  onChange={(event) => setReportSerie(event.target.value)}
                  className="border rounded-md px-3 py-2 w-full text-sm bg-white"
                >
                  <option value="">Todas</option>
                  {series.map((serie) => (
                    <option key={serie} value={serie}>{serie}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Sala</label>
                <select
                  value={reportSala}
                  onChange={(event) => setReportSala(event.target.value)}
                  className="border rounded-md px-3 py-2 w-full text-sm bg-white"
                >
                  <option value="">Todas</option>
                  {salas.map((sala) => (
                    <option key={sala} value={sala}>{sala}</option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-bold mb-4">Distribuição dos tipos</h2>
            {reportDistribution.length === 0 ? (
              <p className="text-muted-foreground">Sem dados para os filtros selecionados.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2 text-sm">
                  {reportDistribution.map((item) => (
                    <div key={item.name} className="flex items-center justify-between border rounded-md px-3 py-2">
                      <span>{item.name}</span>
                      <span className="font-semibold">{item.value}</span>
                    </div>
                  ))}
                </div>
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RePieChart>
                      <Pie data={reportDistribution} dataKey="value" nameKey="name" outerRadius={100} label>
                        {reportDistribution.map((item, index) => (
                          <Cell key={item.name} fill={["#7c3aed", "#2563eb", "#f97316", "#10b981", "#ec4899", "#0ea5e9", "#eab308", "#ef4444", "#22c55e"][index % 9]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </Card>

          {reportAdvancedResults.length === 0 ? (
            <Card className="p-6">
              <p className="text-muted-foreground text-sm">Nenhum resultado do teste avançado para os filtros selecionados.</p>
            </Card>
          ) : (
            <>
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Distribuição por Área Vocacional</h2>
                <p className="text-sm text-muted-foreground mb-4">{reportAdvancedResults.length} aluno(s) com teste avançado</p>
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportHebagDistribution} layout="vertical" margin={{ left: 80 }}>
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={75} />
                      <Tooltip />
                      <Bar dataKey="value" name="Alunos">
                        {reportHebagDistribution.map((item) => (
                          <Cell key={item.area} fill={HEBAG_COLORS[item.area]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Distribuição dos Microperfis</h2>
                {reportMicroperfilDistribution.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Sem dados de microperfil.</p>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-2 text-sm">
                      {reportMicroperfilDistribution.map((item) => (
                        <div key={item.name} className="flex items-center justify-between border rounded-md px-3 py-2">
                          <span className="truncate mr-2">{item.name}</span>
                          <span className="font-semibold shrink-0">{item.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RePieChart>
                          <Pie data={reportMicroperfilDistribution} dataKey="value" nameKey="name" outerRadius={100} label>
                            {reportMicroperfilDistribution.map((_, index) => (
                              <Cell key={index} fill={["#7c3aed", "#2563eb", "#f97316", "#10b981", "#ec4899", "#0ea5e9", "#eab308", "#ef4444", "#22c55e", "#6366f1", "#14b8a6", "#f43f5e"][index % 12]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RePieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </Card>

              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Média HEBAG da turma</h2>
                <p className="text-sm text-muted-foreground mb-4">Pontuação média por área ({reportAdvancedResults.length} aluno(s))</p>
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportHebagAverages}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value: number) => value.toFixed(1)} />
                      <Bar dataKey="value" name="Média">
                        {reportHebagAverages.map((item) => (
                          <Cell key={item.area} fill={HEBAG_COLORS[item.area]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

    </AdminLayout>
  )
}
